import React, { useEffect, useState } from 'react';
import { Check, ChevronRight, Loader2 } from 'lucide-react';
import API_CONFIG from '../config/api';
import { getPlatformMeta, formatMoney } from '../data/taskMeta';
import { useTranslation } from '../i18n/LocaleContext';

// Two looping demo scenes shown in turn:
//  - task flow (5s loop, held 10s): tap -> +$ -> approved -> wallet
//  - withdrawal flow (8s loop, held 8s): wallet -> sheet -> processing -> success
// Both render with live data (real task, real balance) so they mirror the real UI.

const TaskScene = ({ task, t }) => {
  const { label, Icon: PlatformIcon } = getPlatformMeta(task.platform);
  const reward = formatMoney(task.reward);

  return (
    <div className="absolute inset-0 bg-brand-50">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 max-w-[46%]">
        <span className="inline-block rounded bg-white px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-brand-700 ring-1 ring-black/[0.05]">
          {t('flowBadge')}
        </span>
        <p className="mt-1.5 text-[15px] font-extrabold leading-snug text-ink">
          {t('flowTitle', { n: reward })}
        </p>
        <p className="mt-1.5 text-[11px] text-ink-muted">
          {t('flowSub')}
        </p>
      </div>

      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-[196px]">
        <div className="relative bg-white rounded-lg p-2 flex items-center gap-2 ring-1 ring-black/[0.06] shadow-lift">
          <span className="w-10 h-10 shrink-0 rounded-md overflow-hidden ring-1 ring-black/[0.06]">
            <img
              src={API_CONFIG.getImageUrl(task.imageUrl)}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </span>
          <span className="flex-1 min-w-0">
            <span className="line-clamp-1 text-[11px] font-bold text-ink leading-snug">{task.title}</span>
            <span className="mt-1 flex items-center gap-1 text-[10px] text-ink-muted min-w-0">
              <PlatformIcon className="w-3 h-3 shrink-0" />
              <span className="truncate">{task.category || label}</span>
            </span>
          </span>
          <span className="shrink-0 rounded-lg bg-brand-500 px-2 py-1 text-[11px] font-bold text-white tabular">
            +{reward}
          </span>

          <span className="flow-tap absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-brand-500/80" />
          <span className="flow-money absolute right-2 top-1 text-[12px] font-extrabold text-money tabular drop-shadow-sm">
            +{reward}
          </span>
          <span className="flow-check absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-success flex items-center justify-center">
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </span>
        </div>

        <div className="mt-1.5 flex items-center justify-between rounded bg-white px-2 py-1 text-[10.5px] text-ink-muted ring-1 ring-black/[0.06]">
          <span>Số dư ví</span>
          <span className="relative tabular font-bold text-ink">
            <span className="flow-wallet-old">$0.00</span>
            <span className="flow-wallet-new absolute inset-0">{reward}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

// "Screen recording" style: a mini phone screen cuts through 4 real moments —
// wallet -> withdraw sheet -> processing -> success — one 8s take.
const WithdrawScene = ({ amount, t }) => (
  <div className="absolute inset-0 bg-brand-50">
    <div className="absolute inset-y-0 right-0 w-[210px] bg-white/60" />
    <div className="absolute -right-8 -top-12 w-44 h-44 rounded-full bg-brand-500/10 blur-2xl" />

    <div className="absolute left-4 top-1/2 -translate-y-1/2 right-[150px]">
      <span className="inline-block rounded bg-white px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-brand-700 ring-1 ring-black/[0.05]">
        {t('wdBadge')}
      </span>
      <p className="mt-1.5 text-[14px] font-extrabold leading-snug text-ink">
        {t('wdTitle')}
      </p>
      <p className="mt-1.5 text-[11px] text-ink-muted">
        {t('wdSub')}
      </p>
    </div>

    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-[128px] h-[118px] rounded-2xl bg-white ring-1 ring-black/10 overflow-hidden shadow-lift">
      {/* 1 — wallet */}
      <div className="wd-s1 absolute inset-0 p-2">
        <p className="text-[8.5px] text-ink-muted">{t('wdBalanceLabel')}</p>
        <p className="tabular text-[15px] font-extrabold text-ink leading-tight">{amount}</p>
        <div className="relative mt-1.5 h-6 rounded-md bg-brand-500 flex items-center justify-center">
          <span className="text-[9.5px] font-bold text-white">{t('wdBtn')}</span>
          <span className="wd-tap absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white/90" />
        </div>
        <div className="mt-2 space-y-1.5">
          <span className="block h-2 rounded bg-black/[0.06]" />
          <span className="block h-2 w-3/4 rounded bg-black/[0.06]" />
        </div>
      </div>

      {/* 2 — withdraw sheet */}
      <div className="wd-s2 absolute inset-0 p-2">
        <p className="text-[9px] font-bold text-ink">{t('wdBtn')}</p>
        <div className="mt-1 rounded-md border border-black/10 p-1.5">
          <p className="text-[7.5px] text-ink-muted">{t('wdAmountLabel')}</p>
          <p className="tabular text-[12px] font-extrabold text-ink leading-tight">{amount}</p>
        </div>
        <div className="mt-1 flex items-center gap-1 text-[8.5px] text-ink-muted">
          <span className="w-3.5 h-3.5 rounded bg-canvas" />
          <span className="flex-1 truncate">{t('wdBank')}</span>
          <ChevronRight className="w-2.5 h-2.5" />
        </div>
        <div className="relative mt-1.5 h-6 rounded-md bg-brand-500 flex items-center justify-center">
          <span className="text-[9.5px] font-bold text-white">{t('wdConfirm')}</span>
          <span className="wd-tap2 absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white/90" />
        </div>
      </div>

      {/* 3 — processing */}
      <div className="wd-s3 absolute inset-0 flex flex-col items-center justify-center gap-2">
        <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
        <p className="text-[10px] text-ink-secondary">{t('wdProcessing')}</p>
      </div>

      {/* 4 — success */}
      <div className="wd-s4 absolute inset-0 flex flex-col items-center justify-center gap-1.5">
        <span className="w-10 h-10 rounded-full bg-success flex items-center justify-center">
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        </span>
        <p className="text-[10px] font-bold text-ink">{t('wdSuccess')}</p>
        <p className="tabular text-[11px] font-extrabold text-money">+{amount}</p>
      </div>
    </div>
  </div>
);

const PromoBanner = ({ task, balance }) => {
  const { t } = useTranslation();
  const [scene, setScene] = useState(0);

  useEffect(() => {
    if (!task) return undefined;
    // Hold each scene for whole animation cycles so loops never cut mid-way.
    const hold = scene === 0 ? 10000 : 8000;
    const timer = setTimeout(() => setScene((s) => (s + 1) % 2), hold);
    return () => clearTimeout(timer);
  }, [scene, task]);

  if (!task) return null;

  return (
    <div className="max-w-md mx-auto">
      <div className="relative h-[132px] overflow-hidden">
        <div key={scene} className="animate-fade-in absolute inset-0">
          {scene === 0
            ? <TaskScene task={task} t={t} />
            : <WithdrawScene amount={formatMoney(balance > 0 ? balance : 50)} t={t} />}
        </div>

        <span className="absolute bottom-2 right-3 z-10 flex items-center gap-1">
          {[0, 1].map((i) => (
            <span
              key={i}
              className={`h-1 rounded-[2px] transition-all duration-300 ${
                i === scene ? 'w-4 bg-brand-600' : 'w-1.5 bg-brand-600/25'
              }`}
            />
          ))}
        </span>
      </div>
    </div>
  );
};

export default PromoBanner;
